/* Three platform numbers under the hero. Live from landingStats (cached an
   hour server-side), rounded DOWN so the "+" is always true; the countries
   figure is a PostHog geo count refreshed by hand. Counts up once the band
   scrolls into view. */
import { useEffect, useRef, useState } from "react";
import { trpc } from "@/lib/trpc";
import { useTranslation } from "react-i18next";

// Countries with 10+ signed-up accounts (PostHog geo on identified persons,
// 2026-09-02: ~150 of 211 codes seen). Rounded down so the "+" holds.
const COUNTRIES = 150;
// Shown until the query answers (roughly the Sep 2026 totals).
const FALLBACK = { creators: 56000, videoSeconds: 820000 };

function floorTo(n: number, step: number) {
  return Math.max(0, Math.floor(n / step) * step);
}
function roundDown(n: number) {
  if (n >= 1_000_000) return floorTo(n, 100_000);
  if (n >= 100_000) return floorTo(n, 10_000);
  if (n >= 10_000) return floorTo(n, 1_000);
  return floorTo(n, 100);
}

function useCountUp(target: number, active: boolean, ms = 1400) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active) return;
    if (matchMedia("(prefers-reduced-motion:reduce)").matches) {
      setValue(target);
      return;
    }
    let raf = 0;
    const t0 = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / ms);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, active, ms]);
  return value;
}

export default function StatsBand() {
  const { t } = useTranslation();
  const stats = trpc.landingStats.get.useQuery(undefined, {
    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const creators = roundDown(stats.data?.creators ?? FALLBACK.creators);
  const seconds = roundDown(stats.data?.videoSeconds ?? FALLBACK.videoSeconds);

  const ref = useRef<HTMLElement | null>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) {
      setSeen(true);
      return;
    }
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setSeen(true);
          io.disconnect();
        }
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const c = useCountUp(creators, seen);
  const s = useCountUp(seconds, seen);
  const k = useCountUp(COUNTRIES, seen);
  const fmt = (n: number) => n.toLocaleString("en-US");

  return (
    <section className="stats" ref={ref} aria-label={t("landingV2.stats.aria")}>
      <div className="stats-in">
        <div className="stat">
          <span className="n">{fmt(c)}+</span>
          <span className="l">{t("landingV2.stats.creators")}</span>
        </div>
        <div className="stat">
          <span className="n">{fmt(s)}+</span>
          <span className="l">{t("landingV2.stats.seconds")}</span>
        </div>
        <div className="stat">
          <span className="n">{fmt(k)}+</span>
          <span className="l">{t("landingV2.stats.countries")}</span>
        </div>
      </div>
    </section>
  );
}
